import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';
import type { DangerLevel } from './allergies';
import { DANGER_LEVELS } from './allergies';
import { useThemedStyles } from './theme/useThemedStyles';

interface Medication { id: number; name: string }
interface Allergy { id: number; name: string; dangerLevel: DangerLevel; medications: Medication[] }

const toDangerLevel = (value: number | string): DangerLevel => {
  if (typeof value === 'number') {
    return DANGER_LEVELS[value] ?? 'Low';
  }
  return (DANGER_LEVELS.includes(value as DangerLevel) ? value : 'Low') as DangerLevel;
};

const normalizeAllergy = (raw: any): Allergy => ({
  id: raw.id,
  name: raw.name,
  dangerLevel: toDangerLevel(raw.dangerLevel),
  medications: Array.isArray(raw.medications) ? raw.medications.map((m: any) => ({ id: m.id, name: m.name })) : []
});

export default function AllergiesCatalog({ token }: { token: string }) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [list, setList] = useState<Allergy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [level, setLevel] = useState<DangerLevel>('Low');
  const [medName, setMedName] = useState('');
  const [selected, setSelected] = useState<Allergy | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ALLERGIES), { headers: getAuthHeaders(token) });
      if (!res.ok) throw new Error(t('allergies.failedLoad'));
      const data = await res.json();
      const normalized = Array.isArray(data) ? data.map(normalizeAllergy) : [];
      setList(normalized);
      if (selected) {
        const refreshed = normalized.find(a => a.id === selected.id) ?? null;
        setSelected(refreshed);
      }
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };

  // Load-on-token-change behavior is intentional; `load` is recreated per render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const create = async () => {
    if (!name) return;
    setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ALLERGIES), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ name, dangerLevel: DANGER_LEVELS.indexOf(level) })
      });
      if (!res.ok) throw new Error(t('allergies.failedCreate'));
      setName(''); setLevel('Low');
      load();
    } catch (e: any) { setError(e.message); }
  };

  const update = async (a: Allergy) => {
    setError('');
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ALLERGIES}/${a.id}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ name: a.name, dangerLevel: DANGER_LEVELS.indexOf(a.dangerLevel) })
      });
      if (!res.ok) throw new Error(t('allergies.failedUpdate'));
      load();
    } catch (e: any) { setError(e.message); }
  };

  const remove = async (id: number) => {
    if (!window.confirm(t('allergies.deleteAllergyConfirm'))) return;
    setError('');
    try { const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.ALLERGIES}/${id}`), { method: 'DELETE', headers: getAuthHeaders(token) }); if (!res.ok) throw new Error(t('allergies.failedDelete')); load(); }
    catch (e: any) { setError(e.message); }
  };

  const addMedication = async () => {
    if (!selected || !medName) return;
    setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ALLERGY_MEDICATIONS, { allergyId: selected.id }), { method: 'POST', headers: getAuthHeaders(token), body: JSON.stringify({ name: medName }) });
      if (!res.ok) throw new Error(t('allergies.failedAddMedication'));
      setMedName('');
      load();
    } catch (e: any) { setError(e.message); }
  };

  const removeMedication = async (medId: number) => {
    if (!window.confirm(t('allergies.deleteMedicationConfirm'))) return;
    setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ALLERGY_MEDICATION_DELETE, { medId }), { method: 'DELETE', headers: getAuthHeaders(token) });
      if (!res.ok) throw new Error(t('allergies.failedDeleteMed'));
      load();
    }
    catch (e: any) { setError(e.message); }
  };

  const themedInput: React.CSSProperties = { ...theme.input, padding: 8 };
  const themedCompactInput: React.CSSProperties = { ...theme.input, padding: 6 };
  const themedButton: React.CSSProperties = { ...theme.button, padding: '8px 14px' };

  if (loading) return <div style={{ color: theme.text }}>{t('allergies.loadingAllergies')}</div>;
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24, color: theme.text }}>
      <h2>{t('allergies.title')}</h2>
      {error && <div style={{ color: 'red' }}>{t('allergies.error', { message: error })}</div>}

      <div className="responsive-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder={t('allergies.allergyNamePlaceholder')} value={name} onChange={e => setName(e.target.value)} style={themedInput} />
        <select value={level} onChange={e => setLevel(e.target.value as DangerLevel)} style={themedInput}>
          {DANGER_LEVELS.map(l => <option key={l} value={l}>{t(`allergies.levels.${l}`)}</option>)}
        </select>
        <button onClick={create} style={themedButton}>{t('allergies.addAllergy')}</button>
      </div>

      {list.length === 0 ? <div>{t('allergies.noAllergies')}</div> : (
        <div style={{ display: 'grid', gap: 12 }}>
          {list.map(a => (
            <div key={a.id} style={{ ...theme.card, padding: 12 }}>
              <div className="responsive-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input value={a.name} onChange={e => setList(ls => ls.map(x => x.id === a.id ? { ...x, name: e.target.value } : x))} style={{ ...themedCompactInput, minWidth: 200 }} />
                <select value={a.dangerLevel} onChange={e => setList(ls => ls.map(x => x.id === a.id ? { ...x, dangerLevel: e.target.value as DangerLevel } : x))} style={themedCompactInput}>
                  {DANGER_LEVELS.map(l => <option key={l} value={l}>{t(`allergies.levels.${l}`)}</option>)}
                </select>
                <button onClick={() => update(a)} style={{ padding: '6px 12px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6 }}>{t('allergies.save')}</button>
                <button onClick={() => remove(a.id)} style={{ padding: '6px 12px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6 }}>{t('allergies.delete')}</button>
                <button onClick={() => setSelected(a)} style={{ marginLeft: 'auto', padding: '6px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6 }}>{t('allergies.manageMeds')}</button>
              </div>
              {selected?.id === a.id && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${theme.border}` }}>
                  <div className="responsive-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input placeholder={t('allergies.medicationNamePlaceholder')} value={medName} onChange={e => setMedName(e.target.value)} style={themedCompactInput} />
                    <button onClick={addMedication} style={{ padding: '6px 12px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6 }}>{t('allergies.add')}</button>
                  </div>
                  <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                    {a.medications?.length ? a.medications.map(m => (
                      <div className="responsive-row" key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{m.name}</span>
                        <button onClick={() => removeMedication(m.id)} style={{ marginLeft: 'auto', padding: '4px 10px', background: '#f43f5e', color: '#fff', border: 'none', borderRadius: 6 }}>{t('allergies.remove')}</button>
                      </div>
                    )) : <div style={{ opacity: 0.7 }}>{t('allergies.noMedications')}</div>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
